import { useMemoizedFn, useMount } from 'ahooks';
import { Checkbox, Col, Form, Radio, Row, Select } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch } from 'app/hooks';
import { DeleteIcon } from 'components/Icons';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { RevxMeasureNumberInput } from 'components/RevxMeasureInput/RevxMeasureNumberInput';
import { MeasureType } from 'domains/measurementDomain';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { sizeForViewer, useCustomSizeListUnit } from 'hooks/useCustomSizeUnit';
import { UnderlineText } from 'components/RevxTypography';
import {
  CustomRuleCodeEnum,
  OrientationEnum,
  PartialSKU,
  SkuSize,
  SkuSizePairPayload,
  SkuSizePayload,
  StepActionTypeEnum,
  UpdateSize,
} from 'domains/skuDomain';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { organizeStructure } from 'utils/filter';
import { plainOptionHandle } from 'utils/jobConfig';
import { RULE_MEASURE_PRECISION } from 'utils/validators';
import '../SingleWizardStep/Step2Size.less';
import { FormCustomRuleCode } from '../components/FormCustomRuleCode';

const { Option } = Select;

type Props = {
  sku: PartialSKU;
};

type MultiPartSize = {
  wrapperSize: SkuSize;
  partSize: SkuSize;
  id: number;
  skuId: number;
};

export const IndividualSizes = ({ sku }: Props) => {
  const { t } = useTranslation();
  const { columnTitle, displaySize, unit } = useMeasurementUnit();
  const dispatch = useAppDispatch();
  const [form] = useForm();
  // Judged on what the user sees: in metric the form still holds full-precision inches.
  const precisionRule = RULE_MEASURE_PRECISION(3, MeasureType.LENGTH, form);
  const [catchSize, setCatchSize] = useState<Partial<UpdateSize>>({});
  const [foldPanelCount, setFoldPanelCount] = useState<number>(
    Number(sku?.foldPanelCount)
  );
  const [customRuleCode, setCustomRuleCode] = useState(
    sku?.customRuleCode || CustomRuleCodeEnum.NONE
  );
  const [orientationEdit, setOrientationEdit] = useState<number[]>([]);
  useCustomSizeListUnit(form, 'skuSizes');

  useMount(() => {
    form.setFieldsValue({
      skuSizes: defaultForm() || [],
      orientation: sku.orientation,
      foldPanelCount: foldPanelCount,
      customRuleCode: customRuleCode,
      skipSizeAndOrientation: Boolean(sku?.skipSizeAndOrientation),
    });

    dispatch.sku.registerStepAction({
      [StepActionTypeEnum.STEP2_SIZES]: submit,
    });

    setCatchSize(form.getFieldsValue());
  });

  const matchSkuSize = (str1: string, str2: string): boolean => {
    if (
      organizeStructure(str1)?.length &&
      organizeStructure(str1)?.width &&
      organizeStructure(str1)?.length === organizeStructure(str2).length &&
      organizeStructure(str1)?.width === organizeStructure(str2)?.width
    ) {
      return true;
    }
    return false;
  };

  const partSizeIntersection = useMemo(() => {
    const defaultOrientation: OrientationEnum[] = [];
    sku.parts?.forEach((item) => {
      [OrientationEnum.portrait, OrientationEnum.landscape].forEach(
        (orientation) => {
          if (!item.part.orientation.includes(orientation)) {
            defaultOrientation.push(orientation);
          }
        }
      );
    });
    return Array.from(new Set(defaultOrientation));
  }, [sku]);

  const defaultForm = () => {
    const multiPartSize: MultiPartSize[] = [];
    (sku.parts || []).forEach((item) => {
      (item.skuSizePairs || []).forEach((partSize) => {
        multiPartSize.push({
          wrapperSize: partSize.wrapperSize,
          partSize: partSize.partSize,
          id: item.id,
          skuId: item.part.id,
        });
      });
    });

    const defaultFormData: { [x: string]: string | number }[] = [];
    const editList: number[] = [];
    multiPartSize.forEach((item) => {
      (sku.skuSizes || []).forEach((sizeItem, idx) => {
        // The cells are labelled with the viewer's unit, so they have to be filled
        // in it. Filling them from the stored inches is how a metric supplier saw
        // 8.5x11 under a mm header, and saved it back as 8.5x11mm.
        const wrapper = sizeForViewer(
          sizeItem.finishedSize,
          sizeItem.finishedSizeMetric,
          unit
        );
        if (!defaultFormData[idx]) {
          defaultFormData[idx] = {
            finishedLength: organizeStructure(wrapper)?.length || 0,
            finishedWidth: organizeStructure(wrapper)?.width || 0,
            orientation: sizeItem.orientation,
            // orientation:
            //   +(organizeStructure(wrapper)?.length || 0) >=
            //     +(organizeStructure(wrapper)?.width || 0)
            //     ? OrientationEnum.portrait
            //     : OrientationEnum.landscape,
            key: idx,
          };
        }

        if (
          organizeStructure(wrapper)?.length ===
            organizeStructure(wrapper)?.width &&
          organizeStructure(wrapper)?.length &&
          organizeStructure(wrapper)?.width
        ) {
          editList.push(idx);
        }

        // Both sides imperial. `wrapper` is the size in whatever unit the viewer reads, and the
        // stored pairing is always in inches, so comparing the two matched nothing at all in
        // metric: no part selection was pre-filled, and saving a form whose selects were empty
        // dropped every pairing the SKU had. The value put into the form stays imperial too -
        // it is what the part options are matched on when this is submitted.
        if (matchSkuSize(item?.wrapperSize?.finishedSize, sizeItem.finishedSize)) {
          defaultFormData[idx][item.id] = item?.partSize?.finishedSize;
        }
      });
    });

    setOrientationEdit(editList);

    if (!multiPartSize || !multiPartSize.length) {
      (sku.skuSizes || []).forEach((sizeItem, idx) => {
        // The cells are labelled with the viewer's unit, so they have to be filled
        // in it. Filling them from the stored inches is how a metric supplier saw
        // 8.5x11 under a mm header, and saved it back as 8.5x11mm.
        const wrapper = sizeForViewer(
          sizeItem.finishedSize,
          sizeItem.finishedSizeMetric,
          unit
        );
        if (!defaultFormData[idx]) {
          defaultFormData[idx] = {
            finishedLength: organizeStructure(wrapper)?.length || 0,
            finishedWidth: organizeStructure(wrapper)?.width || 0,
            orientation: sizeItem.orientation || OrientationEnum.portrait,
            // orientation:
            //   +(organizeStructure(wrapper)?.length || 0) >=
            //     +(organizeStructure(wrapper)?.width || 0)
            //     ? OrientationEnum.portrait
            //     : OrientationEnum.landscape,
            key: idx,
          };
        }
      });
    }

    if (!defaultFormData.length) {
      return [{}];
    } else {
      return defaultFormData;
    }
  };

  const addSize = () => {
    const val = form.getFieldsValue();
    if (!val.skuSizes) {
      val.skuSizes = [{ key: 1 }];
    }
    val.skuSizes.push({ key: val.skuSizes.length });

    form.setFieldsValue(val);
  };

  const submit = useMemoizedFn(async () => {
    if (sku.readonly) {
      return true;
    }
    const validate = form.getFieldsError();
    validate.forEach((item) => {
      if (item.errors && item.errors.length) {
        return false;
      }
    });
    try {
      const formValue = await form.validateFields();
      const payload: SkuSizePairPayload['updateParams'] = [];
      const payloadSize: UpdateSize['skuSizes'] = [];
      formValue.skuSizes.forEach((item: { [x: string]: number | string }) => {
        const wrapperSizeObj = plainOptionHandle[customRuleCode](
          {
            finishedWidth: Number(item.finishedWidth),
            finishedLength: Number(item.finishedLength),
          },
          foldPanelCount || 0
        );
        const wrapperSize = {
          // Numbers rather than a joined string: the request declares the unit they were typed
          // in, so the API stores that side verbatim and derives the other.
          finishedWidth: wrapperSizeObj?.finishedWidth,
          finishedLength: wrapperSizeObj?.finishedLength,
          flatWidth: wrapperSizeObj?.flatWidth,
          flatLength: wrapperSizeObj?.flatLength,
          orientation: item.orientation as OrientationEnum,
          // orientation:
          //   item.finishedLength > item.finishedWidth
          //     ? OrientationEnum.portrait
          //     : OrientationEnum.landscape,
          widthLengthSize: '',
        };
        const wrapperSignleSize = {
          finishedLength: Number(wrapperSizeObj?.finishedLength),
          finishedWidth: Number(wrapperSizeObj?.finishedWidth),
          flatWidth: Number(wrapperSizeObj?.flatWidth),
          flatLength: Number(wrapperSizeObj?.flatLength),
          orientation: item.orientation as OrientationEnum,
          // orientation:
          //   +item.finishedLength > +item.finishedWidth
          //     ? OrientationEnum.portrait
          //     : OrientationEnum.landscape,
        };
        payloadSize.push(wrapperSignleSize);
        Object.keys(item).forEach((partId) => {
          if (!isNaN(+partId)) {
            let choicePartSkuSize = (sku?.parts || [])
              .filter((item) => item.id === +partId)[0]
              .part.skuSizes.filter(
                (partSkuSize) => partSkuSize?.finishedSize === item[partId + '']
              )[0];

            if (
              payload.filter((payloadItem) => payloadItem.id === +partId).length
            ) {
              payload.forEach((item) => {
                if (item.id === +partId) {
                  item.skuSizePairs?.push({
                    wrapperSize,
                    partSize: choicePartSkuSize,
                  });
                }
              });
            } else {
              payload.push({
                skuSizePairs: [
                  {
                    wrapperSize,
                    partSize: choicePartSkuSize,
                  },
                ],
                id: +partId,
                skuId: (sku?.parts || []).filter(
                  (item) => item.id === +partId
                )[0]?.part?.id,
              });
            }
          }
        });
      });
      if (sku.parts && sku.parts.length) {
        await dispatch.sku.updateSize({
          id: sku.id,
          version: sku.version,
          // The wizard's own selector, not the user's setting: these numbers were typed under
          // the unit shown in the wizard header.
          enteredUnit: unit,
          customRuleCode: formValue.customRuleCode,
          orientation: formValue.orientation,
          skipSizeAndOrientation: formValue.skipSizeAndOrientation,
          skuSizes: payloadSize,
          foldPanelCount: formValue.foldPanelCount,
        });
        return dispatch.sku.updateSkuPartSizeBatch({
          wrapperSkuId: sku.id,
          updateParams: payload,
          // Declared once for the whole batch: `SkuPartSizeBatchUpdateParams` hands it down to
          // each row rather than each row carrying its own.
          enteredUnit: unit,
        });
      } else {
        return await dispatch.sku.updateSize({
          id: sku.id,
          version: sku.version,
          // The wizard's own selector, not the user's setting: these numbers were typed under
          // the unit shown in the wizard header.
          enteredUnit: unit,
          customRuleCode: formValue.customRuleCode,
          orientation: formValue.orientation,
          skipSizeAndOrientation: formValue.skipSizeAndOrientation,
          skuSizes: payloadSize,
        });
      }
    } catch {
      // do nothing when form invalid
      return false;
    }
  });

  const formValuesChange = (
    values: Partial<UpdateSize>,
    allvalue: UpdateSize
  ) => {
    const editList: number[] = [];
    const skuSizes = allvalue.skuSizes.map((item, idx) => {
      let orientation =
        item.finishedLength >= item.finishedWidth
          ? OrientationEnum.portrait
          : OrientationEnum.landscape;
      if (
        item.finishedLength === item.finishedWidth &&
        item.finishedLength &&
        item.finishedWidth
      ) {
        editList.push(idx);
        orientation = item.orientation;
      }
      if (
        (!item.finishedLength || !item.finishedWidth) &&
        allvalue.orientation.length === 1
      ) {
        orientation = allvalue.orientation[0];
      }
      if (orientation !== (catchSize.skuSizes || [])[idx]?.orientation) {
        const clearSize: { [x: string]: string | number } = {};
        for (let i in item) {
          if (!isNaN(+i)) {
            clearSize[i] = '';
          } else {
            clearSize[i] = item[i as 'finishedLength'];
          }
        }
        return {
          ...clearSize,
          orientation,
        };
      } else {
        return {
          ...item,
          orientation,
        };
      }
    });
    setFoldPanelCount(allvalue.foldPanelCount);
    setCustomRuleCode(allvalue.customRuleCode);
    setOrientationEdit(editList);
    form.setFieldsValue({
      ...allvalue,
      skuSizes,
    });
    setCatchSize({
      ...allvalue,
      skuSizes,
    } as Partial<UpdateSize>);
    const validate = form.getFieldsError();
    if (validate.filter((item) => item.errors && item.errors.length).length) {
      form.validateFields();
    }
  };

  return (
    <>
      <div className="single-sku-config-wizard-step-title">
        <span className="brand-green">
          {t('platform.sku.wizard.whichSizes')}
        </span>{' '}
        {t('platform.sku.wizard.willYouSupport')}
      </div>
      <div className="single-sku-config-wizard-step-content">
        <Form
          form={form}
          className="single-sku-config-sizes"
          onValuesChange={formValuesChange}
        >
          <Form.Item
            labelCol={{
              flex: 'none',
            }}
            wrapperCol={{
              flex: 'none',
            }}
            label={t(
              'platform.sku.wizard.quantity.form.label.sizesOrientationSkip'
            )}
            name="skipSizeAndOrientation"
          >
            <Radio.Group disabled={sku.readonly}>
              <Radio value={true}>
                <span>Yes</span>
              </Radio>
              <Radio value={false}>
                <span>No</span>
              </Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="orientation"
            label={t(
              'platform.sku.wizard.quantity.form.label.orientationSupport'
            )}
            rules={[{ type: 'array', required: true }]}
          >
            <Checkbox.Group
              disabled={
                sku.readonly ||
                Boolean(partSizeIntersection.length < 2 && sku.parts?.length)
              }
            >
              <Row>
                <Col span={12}>
                  <Checkbox value="portrait" style={{ lineHeight: '32px' }}>
                    {t('platform.sku.wizard.quantity.form.label.portrait')}
                  </Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="landscape" style={{ lineHeight: '32px' }}>
                    {t('platform.sku.wizard.quantity.form.label.landscape')}
                  </Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label={'Custom rule code'} name="customRuleCode">
            <FormCustomRuleCode disabled={sku.readonly} />
          </Form.Item>
          <Form.Item
            label={t('platform.sku.wizard.size.form.label.foldPanelCount')}
            name="foldPanelCount"
            rules={[{ required: true }]}
          >
            <RevxInputNumber min={0} isInteger disabled={sku.readonly} />
          </Form.Item>
          <table className="size-table">
            <thead>
              <tr>
                <th rowSpan={2}>
                  {t(
                    'platform.sku.wizard.size.form.label.landscapePortraitSize'
                  )}
                </th>
                <th colSpan={2}>
                  {columnTitle(
                    t('platform.sku.wizard.size.form.label.mainSkuSize'),
                    MeasureType.LENGTH
                  )}
                </th>
                {(sku?.parts || []).map((item) => (
                  <th rowSpan={2} key={item.id}>
                    {item.part.name}
                  </th>
                ))}
              </tr>
              <tr>
                <th>{t('common.width')}</th>
                <th>{t('common.length')}</th>
              </tr>
            </thead>
            <tbody>
              <Form.List
                name={'skuSizes'}
                rules={[
                  {
                    validator: async (_, skuSizes) => {
                      try {
                        const skuSizeArr = skuSizes.map(
                          (size: Partial<SkuSizePayload>) =>
                            `${size.finishedLength}${size.finishedWidth}${size.key}`
                        );

                        if (new Set(skuSizeArr).size !== skuSizeArr.length) {
                          return Promise.reject(
                            new Error('Sku size must be unique')
                          );
                        }
                        return Promise.resolve();
                      } catch {
                        return Promise.resolve();
                      }
                    },
                  },
                ]}
              >
                {(fields, { add, remove }, { errors }) =>
                  fields.map((field, idx) => (
                    <Fragment key={field.key}>
                      <tr>
                        <td>
                          <Form.Item
                            {...field}
                            label={''}
                            name={[field.name, 'orientation']}
                            fieldKey={[field.fieldKey, 'orientation']}
                            rules={[
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const orientationList =
                                    getFieldValue('orientation');
                                  if (
                                    orientationList.includes(value) &&
                                    value
                                  ) {
                                    return Promise.resolve();
                                  } else {
                                    return Promise.reject(
                                      new Error(
                                        `Orientation must be ${getFieldValue(
                                          'orientation'
                                        )}`
                                      )
                                    );
                                  }
                                },
                              }),
                            ]}
                          >
                            <Select
                              getPopupContainer={(e) => {
                                return e as HTMLElement;
                              }}
                              disabled={!orientationEdit.includes(idx)}
                            >
                              <Option value={'portrait'}>
                                {t(
                                  'platform.sku.wizard.quantity.form.label.portrait'
                                )}
                              </Option>
                              <Option value={'landscape'}>
                                {t(
                                  'platform.sku.wizard.quantity.form.label.landscape'
                                )}
                              </Option>
                            </Select>
                          </Form.Item>
                        </td>
                        <td>
                          <Form.Item
                            {...field}
                            label={''}
                            name={[field.name, 'finishedWidth']}
                            fieldKey={[field.fieldKey, 'finishedWidth']}
                            rules={[
                              { required: true },
                              { type: 'number', min: 0 },
                              precisionRule,
                            ]}
                          >
                            <RevxMeasureNumberInput
                              type={MeasureType.LENGTH}
                              min={0}
                              disabled={sku.readonly}
                            />
                          </Form.Item>
                        </td>
                        <td>
                          <Form.Item
                            {...field}
                            label={''}
                            name={[field.name, 'finishedLength']}
                            fieldKey={[field.fieldKey, 'finishedLength']}
                            rules={[
                              { required: true },
                              { type: 'number', min: 0 },
                              precisionRule,
                            ]}
                          >
                            <RevxMeasureNumberInput
                              type={MeasureType.LENGTH}
                              min={0}
                              disabled={sku.readonly}
                            />
                          </Form.Item>
                        </td>
                        {(sku?.parts || []).map((item) => (
                          <td>
                            <Form.Item
                              {...field}
                              label={''}
                              name={[field.name, item.id]}
                              fieldKey={[field.fieldKey, item.id]}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select',
                                },
                              ]}
                            >
                              <Select
                                getPopupContainer={(e) => {
                                  return e as HTMLElement;
                                }}
                                style={{ minWidth: 100 }}
                              >
                                {item.part.skuSizes
                                  .filter((item) =>
                                    (
                                      form.getFieldValue([
                                        'skuSizes',
                                        field.fieldKey,
                                        'orientation',
                                      ]) || []
                                    ).includes(item.orientation)
                                  )
                                  .map((size) => (
                                    <Option
                                      value={size?.finishedSize}
                                      key={size?.finishedSize}
                                    >
                                      {/* The value stays imperial - the size is matched on it -
                                          and only the label follows the viewer's unit. */}
                                      {displaySize(size?.finishedSize, {
                                        metric: size?.finishedSizeMetric,
                                        withUnit: false,
                                      })}
                                    </Option>
                                  ))}
                              </Select>
                            </Form.Item>
                          </td>
                        ))}
                        {fields.length > 1 ? (
                          <td
                            className="dynamic-delete-button"
                            onClick={() => remove(field.name)}
                          >
                            <DeleteIcon />
                          </td>
                        ) : null}
                      </tr>
                      {fields.length - 1 === field.name &&
                        errors &&
                        !!errors.length && (
                          <tr style={{ border: 'none' }}>
                            <td
                              style={{ border: 'none' }}
                              colSpan={(sku?.parts || []).length + 2}
                            >
                              <Form.ErrorList
                                className="form-list-error"
                                errors={errors}
                              />
                            </td>
                          </tr>
                        )}
                    </Fragment>
                  ))
                }
              </Form.List>
            </tbody>
          </table>

          <UnderlineText
            color="blue"
            size="small"
            style={{ width: 90 }}
            ellipsis
            onClick={() => addSize()}
          >
            + {t('platform.sku.wizard.size.addSize')}
          </UnderlineText>

          {/* <Form.Item
            label={'Bleed for all sizes'}
            name="defaultQty"
            rules={[
              { required: true },
              { type: 'number', min: 1 },
              RULE_PRECISION(2),
            ]}
          >
            <RevxInputNumber
              style={{ width: 88 }}
              min={1}
              disabled={sku.readonly}
            />
          </Form.Item> */}
        </Form>
      </div>
    </>
  );
};
